
import { useNetwork } from '@/src/contexts';
import { cn, isNumber, isObject, normalizeCurrencyCode } from '@/src/helpers';
import { useExecuteXrplTransaction, useFireblocksVaultAccount, useSelectedTokens } from '@/src/hooks';
import { useTokenizedAssetPools } from '@/src/hooks/apis';
import { Button, Spinner, TradingSection, Separator } from '@/src/ui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { TradingFormValues } from '../../trading.types';
import { createOrderAmounts } from './helpers';
import { BuySell, OrderParameters } from './sections';
import { ORDER_TYPE_STYLES, tradePanelFormSchema, TradePanelFormValues } from './trade-panel.types';

export function TradePanel() {
  const { isTestnet } = useNetwork();
  const { data: vaultAccount } = useFireblocksVaultAccount();
  const { watch } = useFormContext<TradingFormValues>();
  const { baseTokenId, quoteTokenId } = watch();
  const [baseToken, quoteToken] = useSelectedTokens(baseTokenId, quoteTokenId);

  const { data: assetPoolsResponse } = useTokenizedAssetPools();

  // Find the matching asset pool for the selected base token
  const assetPool = useMemo(() => {
    if (!assetPoolsResponse?.objects || !baseToken) {
      return null;
    }

    // Match by token currency code or issuer address
    return assetPoolsResponse.objects.find(
      (pool) =>
        pool.xrpl_issuer_address === baseToken.issuer ||
        normalizeCurrencyCode(pool.token_currency_code) === baseToken.code
    );
  }, [assetPoolsResponse, baseToken]);

  const underlyingBond = assetPool?.underlying_bond;

  const form = useForm<TradePanelFormValues>({
    resolver: zodResolver(tradePanelFormSchema),
    defaultValues: {
      orderType: 'buy',
      limitPrice: '',
      quantity: '',
      total: '',
    },
    mode: 'onChange', // Validate on change for better UX
  });
  const { limitPrice, quantity, total, orderType } = form.watch();

  // Calculate total when limitPrice or quantity changes
  useEffect(() => {
    const price = parseFloat(limitPrice);
    const qty = parseFloat(quantity);

    if (isNumber(price) && isNumber(qty) && price > 0 && qty > 0) {
      const calculatedTotal = (price * qty).toFixed(2);
      form.setValue('total', calculatedTotal, { shouldValidate: true });
    } else if (limitPrice === '' || quantity === '') {
      form.setValue('total', '', { shouldValidate: false });
    }
  }, [limitPrice, quantity, form]);

  const { mutateAsync: executeXrplTransaction, isPending } = useExecuteXrplTransaction();

  const onSubmit = async (data: TradePanelFormValues) => {
    // Validate required data
    if (!baseToken || !quoteToken) {
      toast.error('Token information is missing');
      return;
    }

    if (!vaultAccount?.id) {
      toast.error('Vault account not found');
      return;
    }

    const { takerGets, takerPays } = createOrderAmounts({
      orderType: data.orderType,
      baseToken,
      quoteToken,
      quantity: data.quantity,
      total: data.total,
    });

    // Show loading toast when order creation starts
    const loadingToastId = toast.loading(
      `Creating ${data.orderType === 'buy' ? 'buy' : 'sell'} order...`
    );

    try {
      await executeXrplTransaction({
        transactionType: 'offerCreate',
        // assetId is only used in the SDK to understand if the websocket client connects to testnet or mainnet
        // https://github.com/fireblocks/fireblocks-xrp-sdk/blob/main/src/FireblocksXrpSdk.ts#L75
        assetId: isTestnet ? 'XRP_TEST' : 'XRP',
        vaultAccountId: vaultAccount.id,
        params: {
          sellAmount: takerGets,
          buyAmount: takerPays,
        },
      });

      // Dismiss the loading toast and show success toast
      toast.dismiss(loadingToastId);
      toast.success(`${data.orderType === 'buy' ? 'Buy' : 'Sell'} order created successfully`);

      // Reset form after successful submission
      form.reset({
        orderType: data.orderType, // Keep the same order type
        limitPrice: '',
        quantity: '',
        total: '',
      });
    } catch (err) {
      console.error('Transaction error:', err);

      // Dismiss the loading toast
      toast.dismiss(loadingToastId);

      let errorMessage = 'Failed to create order';
      const errorDescription = 'Please try again or contact support if the issue persists';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (isObject(err) && err !== null && 'message' in err) {
        errorMessage = String(err.message);
      }

      toast.error(errorMessage, {
        description: errorDescription,
      });
    }
  };

  const buttonLabel = isPending
    ? 'Creating order...'
    : `${orderType === 'buy' ? 'Buy' : 'Sell'} ${normalizeCurrencyCode(baseToken?.code || '')}`;

  // Better validation for button state
  const isFormValid = useMemo(() => {
    const price = parseFloat(limitPrice);
    const qty = parseFloat(quantity);
    const totalValue = parseFloat(total);

    return (
      !isNaN(price) && !isNaN(qty) && !isNaN(totalValue) && price > 0 && qty > 0 && totalValue > 0
    );
  }, [limitPrice, quantity, total]);

  const isDisabled = isPending || !isFormValid || !baseToken || !quoteToken;

  // Show spinner if tokens are not loaded
  if (!baseToken || !quoteToken) {
    return <Spinner />;
  }

  return (
    <TradingSection>
      <TradingSection.Header>
        <TradingSection.Title>Trade</TradingSection.Title>
      </TradingSection.Header>
      <TradingSection.Body>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <BuySell />
            <OrderParameters quoteToken={quoteToken} baseToken={baseToken} />
            <Button
              className={cn(
                'w-full cursor-pointer transition-all duration-200',
                ORDER_TYPE_STYLES[orderType],
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
              type="submit"
              disabled={isDisabled}
            >
              {buttonLabel}
            </Button>
          </form>
        </FormProvider>

        {underlyingBond && (
          <>
            <Separator className="my-4" />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Token Asset Information</h4>
              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium text-foreground break-words">{underlyingBond.name}</span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <span className="text-muted-foreground">Symbol:</span>
                  <span className="font-mono text-foreground">{underlyingBond.symbol}</span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <span className="text-muted-foreground">ISIN:</span>
                  <span className="font-mono text-foreground">{underlyingBond.isin}</span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <span className="text-muted-foreground">Coupon Rate:</span>
                  <span className="font-mono text-foreground">{underlyingBond.coupon_rate}%</span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <span className="text-muted-foreground">Maturity:</span>
                  <span className="font-mono text-foreground">
                    {new Date(underlyingBond.maturity_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </TradingSection.Body>
    </TradingSection>
  );
}
